import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import WordMark from '../components/WordMark';
import { Helmet } from 'react-helmet-async';
import { Search, Clock, ArrowRight, X } from 'lucide-react';
import { articles, allTags, getFeaturedArticle } from '../data/articles';

const Resources = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  
  const featuredArticle = getFeaturedArticle();
  
  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const matchesSearch = searchQuery === '' || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTag = !selectedTag || article.tags.includes(selectedTag);
      
      return matchesSearch && matchesTag;
    });
  }, [searchQuery, selectedTag]);
  
  const nonFeaturedArticles = filteredArticles.filter(a => a.id !== featuredArticle.id);

  return (
    <div className="min-h-screen bg-white" data-testid="resources-page">
      <Helmet>
        <title>Resource Centre | Business Networking Insights | matchitup.in</title>
        <meta name="description" content="Expert insights on professional networking, trust, business matching and building meaningful connections in India's startup ecosystem. Free articles by the Match It Up team." />
        <meta property="og:title" content="Resource Centre | Business Networking Insights | matchitup.in" />
        <meta property="og:description" content="Expert insights on professional networking, trust and business matching in India." />
        <meta property="og:url" content="https://matchitup.in/resources" />
        <link rel="canonical" href="https://matchitup.in/resources" />
      </Helmet>
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" state={{ fromLogo: true }} className="flex items-center">
            <WordMark size="sm" />
          </Link>
          <Link 
            to="/"
            className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
          >
            Back to App
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Resource Centre
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Insights on professional networking, trust, and building meaningful connections in India's startup ecosystem.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-10 space-y-4">
          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#E8430A] focus:ring-1 focus:ring-[#E8430A] transition-colors"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Tags */}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                !selectedTag
                  ? 'bg-[#E8430A] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
              data-testid="tag-all"
            >
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                  selectedTag === tag
                    ? 'bg-[#E8430A] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
                data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Article */}
        {!searchQuery && !selectedTag && (
          <Link 
            to={`/resources/${featuredArticle.slug}`}
            className="block mb-12 group"
            data-testid="featured-article"
          >
            <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${featuredArticle.gradient} p-8 sm:p-12`}>
              <div className="absolute inset-0 bg-black/20" />
              <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                  Featured
                </span>
              </div>
              <div className="relative z-10 max-w-2xl">
                <div className="flex flex-wrap gap-2 mb-4">
                  {featuredArticle.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 group-hover:underline decoration-2 underline-offset-4">
                  {featuredArticle.title}
                </h2>
                <p className="text-white/80 text-base sm:text-lg mb-6 line-clamp-2">
                  {featuredArticle.excerpt}
                </p>
                <div className="flex items-center gap-4 text-white/70 text-sm">
                  <span>{featuredArticle.author}</span>
                  <span className="w-1 h-1 bg-white/50 rounded-full" />
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {featuredArticle.readTime}
                  </span>
                </div>
              </div>
              <div className="absolute bottom-8 right-8 sm:bottom-12 sm:right-12 opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight className="w-8 h-8 text-white" />
              </div>
            </div>
          </Link>
        )}

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">No articles found matching your search.</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedTag(null); }}
              className="mt-4 text-[#E8430A] hover:opacity-75 transition-colors"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(searchQuery || selectedTag ? filteredArticles : nonFeaturedArticles).map(article => (
              <Link
                key={article.id}
                to={`/resources/${article.slug}`}
                className="group"
                data-testid={`article-card-${article.id}`}
              >
                <article className="h-full bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 hover:shadow-lg transition-all">
                  {/* Gradient Header */}
                  <div className={`h-32 bg-gradient-to-br ${article.gradient} relative`}>
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
                      {article.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-black/30 backdrop-blur-sm rounded text-white text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#E8430A] transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{article.author}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block p-8 bg-slate-50 border border-slate-200 rounded-2xl">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Ready to build meaningful connections?
            </h3>
            <p className="text-slate-600 mb-6">
              Join India's intent-driven professional network.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors hover:opacity-85"
              style={{ background: '#E8430A' }}
              data-testid="cta-join"
            >
              Get Started
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>&copy; 2026 MatchItUp. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
